import { ApiService } from '@/services';
import {
  User,
  Role,
  InviteDTO,
  Response,
  DataLoaderParams,
  UpdatePasswordDTO,
  JwtDTO,
  PaginatedResponse,
  QueryResponse
} from '@/types';
import gql from 'graphql-tag';

export class UserService extends ApiService {
  public async getUser(id: number): Promise<User> {
    const query = gql`
      query getUserById($id: ID!) {
        user(id: $id) {
          id
          name
          email
          status
          roles {
            id
            name
          }
          permissions {
            id
            name
          }
        }
      }
    `;

    return await this.query({ query, variables: { id } }, 'user');
  }

  public async getUsers(
    params: DataLoaderParams
  ): Promise<QueryResponse<PaginatedResponse<User>>> {
    const query = gql`
      query users($first: Int!, $page: Int!, $filters: DataLoaderParams) {
        users(first: $first, page: $page, filters: $filters) {
          data {
            id
            name
            email
            status
            roles {
              id
              name
            }
            permissions {
              id
              name
            }
          }
          paginatorInfo {
            total
            perPage
            currentPage
            hasMorePages
          }
        }
      }
    `;

    return await this.query<QueryResponse<PaginatedResponse<User>>>({
      query,
      variables: params
    });
  }

  public async invite(invite: InviteDTO): Promise<Response> {
    const mutation = gql`
      mutation inviteUser($email: String, $name: String, $roles: [Int!]!) {
        inviteUser(email: $email, name: $name, roles: $roles) {
          status
          message
        }
      }
    `;
    const resp: Response = await this.mutate(
      { mutation, variables: invite },
      'inviteUser'
    );
    return resp;
  }

  public async resetPassword(email: string): Promise<Response> {
    const mutation = gql`
      mutation resetPassword($email: String) {
        reset_password(email: $email) {
          status
          messages
        }
      }
    `;
    const resp: Response = await this.pubMutate(
      { mutation, variables: { email } },
      'reset_password'
    );
    return resp;
  }

  public async forgotPassword(email: string): Promise<Response> {
    const mutation = gql`
      mutation forgotPassword($email: String!) {
        forgotPassword(email: $email) {
          status
          message
        }
      }
    `;
    const resp: Response = await this.pubMutate(
      { mutation, variables: { email } },
      'forgotPassword'
    );
    return resp;
  }

  public async getRoles(): Promise<Role[]> {
    const query = gql`
      query roles {
        roles {
          id
          name
        }
      }
    `;
    return await this.query<Role[]>({ query }, 'roles');
  }

  public async updateUser(user: User): Promise<User> {
    const mutation = gql`
      mutation updateUser($input: UserInput) {
        updateUser(input: $input) {
          id
          name
          email
          status
          roles {
            id
            name
          }
          permissions {
            id
            name
          }
        }
      }
    `;

    return await this.mutate(
      { mutation, variables: { input: user } },
      'updateUser'
    );
  }

  public async updatePassword(dto: UpdatePasswordDTO): Promise<JwtDTO> {
    const mutation = gql`
      mutation updatePassword(
        $id: ID!
        $password: String!
        $new_password: String!
      ) {
        updatePassword(
          id: $id
          password: $password
          new_password: $new_password
        ) {
          status
          token
          exp
          message
          user {
            id
            name
            email
            status
            roles {
              id
              name
            }
            permissions {
              id
              name
            }
          }
        }
      }
    `;

    return await this.mutate({ mutation, variables: dto }, 'updatePassword');
  }

  public async deleteUser(id: number): Promise<any> {
    const mutation = gql`
      mutation deleteUser($id: ID!) {
        deleteUser(id: $id) {
          status
        }
      }
    `;
    const data = await this.mutate(
      { mutation, variables: { id } },
      'deleteUser'
    );
    return data.status;
  }
}

export const userSvc: UserService = new UserService();
